import { IAdminUser } from '@dadohr/shared/domainTypes/adminUser';
import { ICurrentUser } from '@dadohr/shared/domainTypes/currentUser';
import { Doc } from '@dadohr/shared/domainTypes/document';
import firebase from 'firebase/app';
import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { usePromise } from '../../hooks/usePromise';
import { useStateObject } from '../../hooks/useStateObject';
import * as stackdriver from '../../stackdriverErrorReporter';
import { adminUserRef } from '../adminUsers';
import { toAdminUserDoc } from '../adminUsers/documents';
import {
  createCurrentUser,
  getCurrentUser,
  setCurrentUser
} from '../currentUser';
import { callFirebaseFunction } from '../firebaseFunctions';
import { localJsonStorage } from '../localJsonStorage';
import { orgStore } from '../orgs';

const toKey = (type: string, id: string) => `${type}-${id}`;

const USER_CACHE_PREFIX = 'user';

export const signOut = async () => {
  try {
    const currentUser = getCurrentUser();
    localJsonStorage.removeItem(toKey(USER_CACHE_PREFIX, currentUser.id));
    await fetch('/api/session/clear');
  } catch {}
  firebase.auth().signOut();
  window.location.assign('/');
};

type LoginState = {
  currentUser: ICurrentUser | null;
  status:
    | 'INIT'
    | 'CREATING'
    | 'SUCCESS'
    | 'NO_LOGIN'
    | 'NO_USER'
    | 'NO_ORGS'
    | 'NO_ORG'
    | 'ERROR';
};

export const useLogin = (forcedOrgId?: string) => {
  const [state, mergeState] = useStateObject<LoginState>({
    currentUser: null,
    status: 'INIT'
  });

  const [authUser, initialising] = useAuthState(firebase.auth());
  const [user, setUser] = useState<Doc<IAdminUser> | null>(null);

  const update = (
    currentUser: ICurrentUser | null,
    status: LoginState['status']
  ) => {
    setCurrentUser(currentUser);
    mergeState({ status, currentUser });
    stackdriver.setUser(currentUser?.id || '');
    firebase.analytics().setUserId(currentUser?.id || '');
  };

  useEffect(() => {
    if (initialising) {
      return;
    }
    if (!authUser) {
      update(null, 'NO_LOGIN');
      return;
    }

    return adminUserRef(authUser.uid).onSnapshot(
      (s) => {
        if (!s.exists) {
          setUser(null);
          update(null, 'NO_USER');
          return;
        }
        const adminUser = toAdminUserDoc(s);
        if (!adminUser.data.orgs.length) {
          setUser(null);
          update(null, 'NO_ORGS');
        }
        setUser(adminUser);
      },
      (err) => {
        console.error(err);
        setUser(null);
        update(null, 'ERROR');
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, initialising, forcedOrgId || null]);

  useEffect(() => {
    if (authUser) {
      checkAdminClaim(authUser).then((isAdmin) => {
        if (isAdmin) {
          return authUser.getIdToken().then((idToken) => {
            fetch('/api/session/update-super-admin', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ idToken })
            });
          });
        }
      });
    }
  }, [authUser]);

  useEffect(() => {
    if (!user || !authUser) {
      return;
    }
    return orgStore(forcedOrgId || user.data.orgs[0]).listen(
      ({ value: org }) => {
        if (!org) {
          update(null, 'NO_ORGS');
          return;
        }
        if (!user || !authUser) {
          // race condition - cleanup happens elsewhere
          return;
        }

        const currentUser = createCurrentUser(
          authUser,
          user.data,
          org.data,
          org.data.tz
        );

        update(currentUser, 'SUCCESS');
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, user]);

  return state;
};

export const checkAdminClaim = (user: firebase.User) => {
  return user
    .getIdTokenResult()
    .then((result) => !!result.claims.admin)
    .catch(() => false);
};

export const useAdminClaim = () => {
  return usePromise(async () => {
    const authUser = await firebase.auth().currentUser;
    if (!authUser) {
      return false;
    }
    return checkAdminClaim(authUser);
  }, []);
};

export const checkSession = () => {
  return callFirebaseFunction<{ status: 'VALID' | 'INVALID' }>(
    'adminUser-checkSession',
    {}
  ).catch(() => ({ status: 'INVALID' as const }));
};

export const signInWithEmailAndPassword = async (
  email: string,
  password: string
) => {
  const res = await firebase.auth().signInWithEmailAndPassword(email, password);
  return res;
};

/*

Store session information for user in db
- identify by... user agent + location + obfuscated ip info
- if user does not have a valid session -> log out
- update users session information periodically if they are still on the same computer
- extend their deadline
- this can happen in the background, pinging a CF
- whenever we get new login information in authState, check if the session is still valid
  - do this in the background, so that we don't have to excessively wait
- we also need to check if the users tab is active or not
- when a tab becomes active again, also check for session validity

*/
